const MOJIBAKE_LEAD_CHARACTERS = /[\u00c2\u00c3\u00e2\u00ef\u00f0]/u;
const C1_CONTROL_CHARACTERS = /[\u0080-\u009f]/gu;
// File controls are deliberately matched and removed before persistence.
// eslint-disable-next-line no-control-regex
const UNSAFE_FILE_NAME_CHARACTERS = /[\u0000-\u001f\u007f-\u009f]/gu;

const mojibakeScore = (value: string): number => {
	const leadCharacters =
		value.match(/[\u00c2\u00c3\u00e2\u00ef\u00f0]/gu)?.length || 0;
	const controlCharacters = value.match(C1_CONTROL_CHARACTERS)?.length || 0;
	const replacementCharacters = value.match(/\ufffd/gu)?.length || 0;
	return leadCharacters * 2 + controlCharacters * 3 + replacementCharacters * 5;
};

/**
 * Multer/busboy can expose a UTF-8 multipart filename as Latin-1 text. Repair
 * that specific mojibake only when the decoded candidate is demonstrably less
 * suspicious, so legitimate accented names remain untouched.
 */
export const repairPdfFileNameEncoding = (value: string): string => {
	const normalized = value.normalize('NFC');
	if (!MOJIBAKE_LEAD_CHARACTERS.test(normalized)) return normalized;
	if ([...normalized].some((character) => character.codePointAt(0)! > 0xff)) {
		return normalized;
	}

	try {
		const latin1Bytes = Uint8Array.from(normalized, (character) =>
			character.charCodeAt(0)
		);
		const repaired = new TextDecoder('utf-8', { fatal: true })
			.decode(latin1Bytes)
			.normalize('NFC');
		return mojibakeScore(repaired) < mojibakeScore(normalized) ? repaired : normalized;
	} catch {
		return normalized;
	}
};

/** Normalize an uploaded filename before it is stored or sent to a remote API. */
export const normalizeUploadedPdfFileName = (value: string): string => {
	const repaired = repairPdfFileNameEncoding(value)
		.replace(UNSAFE_FILE_NAME_CHARACTERS, '')
		.replace(/[\\/]/gu, '-')
		.trim();
	return repaired || 'document.pdf';
};

/** Format stored filenames for users, including records created before the fix. */
export const formatPdfDisplayName = (value?: string): string => {
	if (!value?.trim()) return 'PDF document';
	return repairPdfFileNameEncoding(value.trim()).replace(/_\d{10,}(?=\.[^.]+$)/u, '');
};
